import { getFirebaseConfig } from '../firebaseConfig';
import { setAssetOverrides, type AssetOverride } from './overrides';

// Firestore-backed source for the asset override map. Reads the assetOverrides
// collection ONCE at boot via firebase/firestore/lite (no realtime listener -
// a one-shot read is all the game needs). Firebase is imported dynamically so
// it stays out of the main chunk and is never fetched when the project is not
// configured.
//
// Hard rule: this must NEVER throw. A missing config or a backend hiccup falls
// back to bundled assets - the game must boot regardless.

// Shape of one assetOverrides doc the studio writes. Read defensively - bad
// rows are skipped, not fatal.
type OverrideDoc = {
  key?: string;
  url?: string;
  cols?: number;
  rows?: number;
  autoTrim?: boolean;
};

export async function loadAssetOverrides(): Promise<void> {
  const config = getFirebaseConfig();
  if (!config) return; // not configured yet - run on bundled assets

  try {
    const { initializeApp, getApps } = await import('firebase/app');
    const { getFirestore, collection, getDocs } = await import('firebase/firestore/lite');

    const app = getApps()[0] ?? initializeApp(config);
    const db = getFirestore(app);
    const snap = await getDocs(collection(db, 'assetOverrides'));

    const map = new Map<string, AssetOverride>();
    snap.forEach((doc) => {
      const d = doc.data() as OverrideDoc;
      if (!d.key || !d.url) return;
      map.set(d.key, { url: d.url, cols: d.cols, rows: d.rows, autoTrim: d.autoTrim });
    });
    setAssetOverrides(map);
  } catch (err) {
    // Backend unreachable / misconfigured - keep bundled defaults.
    console.warn('[overrides] failed to load asset overrides; using bundled assets', err);
  }
}
